import React, {
    useCallback,
    useEffect,
    useState
} from "react";

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    Tooltip,
    IconButton,
    Typography
} from "@mui/material";


import CopyrightIcon from '@mui/icons-material/Copyright';
import VisibilityIcon from "@mui/icons-material/Visibility";

import AxiosInstance from "../../services/AxiosInstance";
import {
    CONFIGURATION_ITEM_URL,
    CONFIGURATION_ITEM_HISTORY_URL
} from "../../Constants/Constants";

import AppTableView from "../AppTableView";
import ConfigurationItemDialog from "./ConfigurationItemDialog";
import ConfigurationItemRelationshipDialog from "./ConfigurationItemRelationshipDialog";
import ConfigurationItemHistoryDialog from "./ConfigurationItemHistoryDialog";


const ConfigurationItem = () => {

    const [data, setData] = useState([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState(null);


    /*
     * Load Configuration Items
     */
    const getConfigurationItems =
        useCallback(async () => {

            setLoading(true);
            setError(null);

            try {

                const response =
                    await AxiosInstance.get(
                        CONFIGURATION_ITEM_URL
                    );

                setData(
                    response.data || []
                );

            } catch (error) {

                console.error(
                    "Error fetching Configuration Items:",
                    error
                );

                setError(
                    "Failed to load Configuration Items. Please try again."
                );

            } finally {

                setLoading(false);
            }

        }, []);


    /*
     * Initial load
     */
    useEffect(() => {

        getConfigurationItems();

    }, [getConfigurationItems]);


    const renderTextWithTooltip = (value) => {
        const text = value || "-";

        return (
            <Tooltip title={text} arrow>
                <Typography
                    variant="body2"
                    noWrap
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "default",
                    }}
                >
                    {text}
                </Typography>
            </Tooltip>
        );
    };

    /*
     * Table columns
     */
    const columns = [

        // {
        //     field: "id",
        //     flex: 0.7,
        //     headerName: "ID",
        //     renderCell: (params) =>
        //         params.value
        // },


        // {
        //     field: "name",
        //     flex: 2,
        //     headerName: "CI Name",
        //     renderCell: (params) =>
        //         params.value
        // },
        ,
        {
            field: "action",
            flex: 1,
            headerName: "Actions",
            disableColumnMenu: true,
            sortable: false,

            renderCell: (params) => (

                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center"
                    }}
                >

                    <ConfigurationItemDialog
                        configurationItemId={
                            params.row.id
                        }
                        configurationItemName={params.row.name}
                        variant="link"
                        onSuccess={
                            getConfigurationItems
                        }
                    />

                </Box>
            )
        },

        {
            field: "configurationCategoryId",
            flex: 1.5,
            headerName: "Category",
            renderCell: (params) =>
                params.row.configurationCategory?.name || "-",

        },


        {
            field: "configurationEnvironmentId",
            flex: 1.5,
            headerName: "Environment",
            renderCell: (params) =>
                params.row.configurationEnvironment?.name || "-",
        },


        {
            field: "impactId",
            flex: 1,
            headerName: "Criticality",
            renderCell: (params) =>
                params.row.impact?.name || "-",
        },


        {
            field: "owner",
            flex: 1.5,
            headerName: "Owner",
            renderCell: (params) =>
                params.value
        },
        {
            field: "configurationResolverGroups",
            flex: 1.8,
            headerName: "Resolver Groups",
            renderCell: (params) => {
                const groups = Array.isArray(params.row.configurationResolverGroups)
                    ? params.row.configurationResolverGroups
                    : [];

                const groupNames = groups
                    .map(group => group?.name)
                    .filter(Boolean)
                    .join(", ");

                // Wrap the returned component in a flexbox centered container
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%', // Fills the cell height to make alignment work
                            width: '100%',
                            overflow: 'hidden' // Prevents text bleeding out of bounds
                        }}
                    >
                        {renderTextWithTooltip(groupNames)}
                    </Box>
                );
            },
        },

        {
            field: "assets",
            flex: 0.9,
            headerName: "Linked Assets",
            renderCell: (params) => {

                const count =
                    Array.isArray(params.row.assets)
                        ? params.row.assets.length
                        : 0;

                return count === 1
                    ? "1 Asset"
                    : `${count} Assets`;
            },
        },

        {
            field: "organizationId",
            flex: 1.5,
            headerName: "Organization",
            renderCell: (params) =>
                params.row.organizationDetails?.name || "-",
        },

        {
            field: "description",
            flex: 2,
            headerName: "Description",
            renderCell: (params) => {
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%', // Fills the cell height to make alignment work
                            width: '100%',
                            overflow: 'hidden' // Prevents text bleeding out of bounds
                        }}
                    >
                        {renderTextWithTooltip(params.value || "")}
                    </Box>
                );
            }
        },

        {
            field: "relationships",
            flex: 1,
            headerName: "Relationships",
            sortable: false,
            renderCell: (params) => (
                <ConfigurationItemRelationshipDialog
                    configurationItemId={params.row.id}
                    configurationItemName={params.row.name}
                    variant="icon"
                />
            )
        },

        {
            field: "history",
            flex: 1,
            headerName: "History",
            disableColumnMenu: true,
            sortable: false,

            renderCell: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center"
                    }}
                >

                    {/* <ConfigurationItemDialog
                        configurationItemId={params.row.id}
                        configurationItemName="View"
                        variant="link"
                        viewMode
                        onSuccess={getConfigurationItems}
                    /> */}

                    <ConfigurationItemHistoryDialog
                        configurationItemId={params.row.id}
                    />

                </Box>
            )
        }

    ];


    /*
     * Error screen
     */
    if (error) {

        return (

            <Container>

                <Alert
                    severity="error"
                    action={

                        <Button
                            color="inherit"
                            size="small"
                            onClick={
                                getConfigurationItems
                            }
                        >
                            Retry
                        </Button>

                    }
                    sx={{
                        borderRadius: 2
                    }}
                >

                    {error}

                </Alert>

            </Container>
        );
    }


    return (

        <Box
            sx={{
                p: 2,
                pt: 0,
                width: "100%"
            }}
        >

            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent:
                        "space-between",
                    alignItems: "center",
                    mb: 2
                }}
            >

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                    }}
                >

                    <CopyrightIcon
                        color="primary"
                    />

                    <Box
                        component="span"
                        sx={{
                            fontSize: 20,
                            fontWeight: 600
                        }}
                    >
                        Configuration Items
                    </Box>

                </Box>


                {/* Add Configuration Item */}
                <ConfigurationItemDialog
                    configurationItemId=""
                    configurationItemName=
                    "Add Configuration Item"
                    variant="button"
                    onSuccess={
                        getConfigurationItems
                    }
                />

            </Box>


            <Paper
                sx={{
                    width: "100%",
                    mb: 2
                }}
            >

                {loading ? (

                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        minHeight="300px"
                        mt={4}
                        mb={4}
                    >

                        <CircularProgress
                            size={48}
                            thickness={4}
                            color="primary"
                        />

                        <Box
                            mt={2}
                            color="text.secondary"
                            fontSize={16}
                        >
                            Loading Configuration Items,
                            please wait...
                        </Box>

                    </Box>

                ) : (

                    <AppTableView
                        idKey="id"
                        rows={data}
                        cols={columns}
                        paginationMode="client"
                        sortMode="client"
                        initialPageSize={15}
                    />

                )}

            </Paper>

        </Box>
    );
};


export default ConfigurationItem;